import { useEffect, useRef, useState, type DragEvent } from "react";
import s from "./CustomizeMenu.module.css";
import { useNavLayout } from "./NavLayoutContext";
import {
  addGroup,
  defaultLayout,
  isDefaultLayout,
  labelOf,
  MAX_NAME,
  moveGroup,
  moveScreen,
  normalizeLayout,
  placeScreen,
  removeGroup,
  renameGroup,
  renameScreen,
  SCREEN_BY_ROUTE,
  toggleHidden,
  type NavLayout,
} from "./navigation";
import { Button, Input, Select } from "../design/components";
import { t, tn } from "../i18n";

const DRAG_TYPE = "application/x-sky-screen";

/**
 * Arrange the menu: order the suites and the screens in them, move screens
 * between suites, rename either, create and delete suites, hide screens.
 *
 * Every move has two ways to make it. Dragging is quick with a mouse and does
 * nothing on a phone, so each row also has up and down buttons and a "Move to"
 * list — the same edit, reachable by touch and by keyboard.
 *
 * Nothing is lost by rearranging. Deleting a suite moves its screens to the
 * one above; a hidden screen still opens from a link; and a screen added to
 * the platform after this layout was saved appears in its default suite.
 */
export function CustomizeMenuDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const { layout, store, save, reset } = useNavLayout();
  const [draft, setDraft] = useState<NavLayout>(layout);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ group: string; index: number } | null>(null);
  const [focusGroup, setFocusGroup] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup — see CreateWorkOrderDialog: StrictMode would close it as it opens.
  }, []);

  const changed = JSON.stringify(normalizeLayout(draft)) !== JSON.stringify(normalizeLayout(layout));

  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isDefaultLayout(draft)) await reset();
      else await save(draft);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDragOverRow = (e: DragEvent, group: string, index: number) => {
    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const after = e.clientY > box.top + box.height / 2;
    setDropAt({ group, index: after ? index + 1 : index });
  };

  const onDrop = (e: DragEvent, group: string, fallbackIndex: number) => {
    const to = e.dataTransfer.getData(DRAG_TYPE);
    if (!to) return;
    e.preventDefault();
    const target = dropAt?.group === group ? dropAt.index : fallbackIndex;
    // Dropping lower in its own suite: the index counts the row being moved.
    const current = draft.groups.find((g) => g.id === group)?.screens.indexOf(to) ?? -1;
    setDraft(placeScreen(draft, to, group, current >= 0 && current < target ? target - 1 : target));
    setDragging(null);
    setDropAt(null);
  };

  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} aria-label={t("Customize the menu")}>
      <div className={s.head}>
        <div>
          <h2 className={s.title}>{t("Customize the menu")}</h2>
          <div className={s.sub}>
            {t(
              "Order the suites and their screens, move screens between suites, rename anything, add your own suites.",
            )}{" "}
            {store === "platform" ? (
              <b>{t("Saved to your account on the platform — the same on every device you sign in on.")}</b>
            ) : (
              <b className={s.warn}>
                {t("The platform is not keeping settings for this sign-in, so this is saved in this browser only.")}
              </b>
            )}
          </div>
        </div>
        <div className={s.headActions}>
          <Button
            onClick={() => {
              const next = addGroup(draft);
              setDraft(next.layout);
              setFocusGroup(next.id);
            }}
          >
            {t("+ New suite")}
          </Button>
          <Button variant="ghost" onClick={() => setDraft(defaultLayout())} disabled={isDefaultLayout(draft)}>
            {t("Reset to default")}
          </Button>
        </div>
      </div>

      <div className={s.groups}>
        {draft.groups.map((group, gi) => {
          const previous = draft.groups[gi - 1] ?? draft.groups[gi + 1];
          return (
            <section
              key={group.id}
              className={s.group}
              aria-label={t("Suite {name}", { name: group.name || t("untitled") })}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes(DRAG_TYPE) && group.screens.length === 0) {
                  e.preventDefault();
                  setDropAt({ group: group.id, index: 0 });
                }
              }}
              onDrop={(e) => onDrop(e, group.id, group.screens.length)}
            >
              <div className={s.groupHead}>
                <Input
                  className={s.groupName}
                  value={group.name}
                  maxLength={MAX_NAME}
                  placeholder={t("Suite name")}
                  aria-label={t("Suite name")}
                  autoFocus={focusGroup === group.id}
                  onFocus={(e) => focusGroup === group.id && e.currentTarget.select()}
                  onChange={(e) => setDraft(renameGroup(draft, group.id, e.target.value))}
                />
                <span className={s.count}>{tn(group.screens.length, "{n} screen", "{n} screens")}</span>
                <div className={s.tools}>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t("Move {name} up", { name: group.name })}
                    title={t("Move suite up")}
                    disabled={gi === 0}
                    onClick={() => setDraft(moveGroup(draft, group.id, -1))}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t("Move {name} down", { name: group.name })}
                    title={t("Move suite down")}
                    disabled={gi === draft.groups.length - 1}
                    onClick={() => setDraft(moveGroup(draft, group.id, 1))}
                  >
                    ↓
                  </Button>
                  {confirmDelete === group.id ? (
                    <>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setDraft(removeGroup(draft, group.id));
                          setConfirmDelete(null);
                        }}
                      >
                        {group.screens.length
                          ? t("Delete — screens go to {name}", { name: previous?.name || t("the next suite") })
                          : t("Delete")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                        {t("Keep")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={draft.groups.length === 1}
                      title={
                        draft.groups.length === 1 ? t("The menu needs at least one suite") : t("Delete this suite")
                      }
                      onClick={() =>
                        group.screens.length ? setConfirmDelete(group.id) : setDraft(removeGroup(draft, group.id))
                      }
                    >
                      {t("Delete")}
                    </Button>
                  )}
                </div>
              </div>

              {group.screens.length === 0 ? (
                <div className={`${s.empty} ${dropAt?.group === group.id ? s.emptyActive : ""}`}>
                  {t("Empty — drag a screen here, or use “Move to” on any screen.")}
                </div>
              ) : (
                <ol className={s.screens}>
                  {group.screens.map((to, si) => {
                    const def = SCREEN_BY_ROUTE.get(to);
                    if (!def) return null;
                    const hidden = draft.hidden.includes(to);
                    const renamed = draft.labels[to] != null && draft.labels[to].trim() !== "";
                    const marker = dropAt?.group === group.id ? dropAt.index : -1;
                    return (
                      <li
                        key={to}
                        className={[
                          s.screen,
                          hidden ? s.screenHidden : "",
                          dragging === to ? s.screenDragging : "",
                          marker === si ? s.dropBefore : "",
                          marker === si + 1 && si === group.screens.length - 1 ? s.dropAfter : "",
                        ].join(" ")}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(DRAG_TYPE, to);
                          e.dataTransfer.effectAllowed = "move";
                          setDragging(to);
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setDropAt(null);
                        }}
                        onDragOver={(e) => onDragOverRow(e, group.id, si)}
                        onDrop={(e) => {
                          e.stopPropagation();
                          onDrop(e, group.id, si);
                        }}
                      >
                        <span className={s.grip} aria-hidden="true" title={t("Drag to move")}>
                          ⠿
                        </span>
                        <svg
                          viewBox="0 0 24 24"
                          width="15"
                          height="15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          aria-hidden="true"
                        >
                          <path d={def.icon} />
                        </svg>
                        <Input
                          className={s.screenName}
                          value={labelOf(draft, to)}
                          maxLength={MAX_NAME}
                          aria-label={t("Name for {screen}", { screen: t(def.label) })}
                          title={
                            renamed ? t("Originally “{screen}”", { screen: t(def.label) }) : t("Rename this screen")
                          }
                          onChange={(e) => setDraft(renameScreen(draft, to, e.target.value, { typing: true }))}
                          onBlur={(e) => !e.target.value.trim() && setDraft(renameScreen(draft, to, ""))}
                        />
                        {renamed && (
                          <button
                            type="button"
                            className={s.linkish}
                            onClick={() => setDraft(renameScreen(draft, to, ""))}
                            title={t("Back to “{screen}”", { screen: t(def.label) })}
                          >
                            {t("undo name")}
                          </button>
                        )}
                        <label
                          className={s.show}
                          title={hidden ? t("Hidden from the menu; still opens from a link") : t("Shown in the menu")}
                        >
                          <input type="checkbox" checked={!hidden} onChange={() => setDraft(toggleHidden(draft, to))} />
                          {t("Show")}
                        </label>
                        <div className={s.tools}>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={t("Move {name} up", { name: t(def.label) })}
                            disabled={gi === 0 && si === 0}
                            onClick={() => setDraft(moveScreen(draft, to, -1))}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={t("Move {name} down", { name: t(def.label) })}
                            disabled={gi === draft.groups.length - 1 && si === group.screens.length - 1}
                            onClick={() => setDraft(moveScreen(draft, to, 1))}
                          >
                            ↓
                          </Button>
                          <Select
                            className={s.moveTo}
                            value={group.id}
                            aria-label={t("Move {screen} to suite", { screen: t(def.label) })}
                            onChange={(e) => setDraft(placeScreen(draft, to, e.target.value))}
                          >
                            {draft.groups.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.id === group.id
                                  ? t("In: {name}", { name: g.name || t("untitled") })
                                  : t("Move to: {name}", { name: g.name || t("untitled") })}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          );
        })}
      </div>

      <div className={s.foot}>
        {error && (
          <span className={s.error} role="alert">
            {t("Not saved: {error}", { error })}
          </span>
        )}
        <span className={s.footNote}>{t("Hidden screens: {n}", { n: draft.hidden.length })}</span>
        <Button onClick={onClose} disabled={busy}>
          {t("Cancel")}
        </Button>
        <Button variant="primary" onClick={() => void commit()} disabled={busy || !changed}>
          {busy ? t("Saving…") : changed ? t("Save menu") : t("No changes")}
        </Button>
      </div>
    </dialog>
  );
}
